import { useEffect, useMemo, useRef, useState } from "react";
import { RankHeatmapTable } from "@/components/rank-tracker/RankHeatmapTable";
import { KeywordRankDetailModal, type RankDetailTarget } from "@/components/rank-tracker/KeywordRankDetailModal";
import { SignalAIRankReport } from "@/components/rank-tracker/SignalAIRankReport";
import { DateRangePicker } from "@/components/shared/DateRangePicker";
import { ShareLinkModal } from "@/components/shared/ShareLinkModal";
import { AddKeywordsModal } from "@/components/shared/AddKeywordsModal";
import { PageTitle } from "@/components/shared/PageTitle";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, MoreVertical, Share2, Sparkles } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useAppData } from "@/contexts/AppDataContext";
import { EmptyBrandState } from "@/components/shared/EmptyBrandState";
import { rankTrackerApi } from "@/resources/rank-tracker";
import { productApi } from "@/resources/product";
import { resolveDateRange, handleApiError } from "@/utils";
import { toast as sonnerToast } from "sonner";
import type { PublicRankTrackerPagePayload } from "@/types";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { KeywordMasterListAction } from "@/constants";

/**
 * WEB-008: the only window control on `/rank-tracker` now that WEB-002 removed
 * `BrandCorrelationSummary` (which owned the page's only `DateRangePicker`). Four fixed presets,
 * nothing wider than 90 days and no custom entry point (NFR-1: `BACKFILL_RANK_TRACKER_KEYWORDS`
 * seeds 90 days of history, and the backend 422s a wider span) -- `allowCustom` is false by
 * construction, not by a flag, because this control has no affordance that could produce a value
 * outside this list.
 */
const WINDOW_PRESETS = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 60, label: "Last 60 days" },
  { days: 90, label: "Last 90 days" },
] as const;
const DEFAULT_WINDOW_DAYS = 30;
const MAX_WINDOW_DAYS = 90;

// No custom-range entry point exists on this screen -- kept only because `resolveDateRange` and
// `PublicRankTrackerPagePayload.filters.customDateRange` still expect the shape. It can never
// hold a value, so this is a constant rather than state.
const NO_CUSTOM_RANGE: { from: Date | undefined; to: Date | undefined } = {
  from: undefined,
  to: undefined,
};

/**
 * Belt over an already-buttoned coat (NFR-1): `WINDOW_PRESETS` already offers nothing above 90
 * days, so this can only ever fire against a value the picker itself could not have produced -- a
 * stale persisted value, or a preset added above 90 without updating this file. Clamping once,
 * here, keeps that mistake from ever reaching `useGetDailyRanks` and tripping the backend's 422.
 */
function clampWindowDays(days: number): number {
  return Math.min(days, MAX_WINDOW_DAYS);
}

/**
 * `resolveDateRange` fixes `to = today` and sets `from = today − days`, which is a `days + 1`
 * calendar-day span counted inclusively. `ListDailyRanksRequest` (backend/app/Http/Requests) also
 * counts inclusively and rejects a span over 90, so handing it a preset's own label value produces
 * one day too many -- "Last 90 days" became a 91-day span and 422'd. Subtracting one here (after
 * the belt-and-braces clamp above, so a stale value over 90 still lands on the max rather than one
 * day past it) makes the inclusive span equal to the preset's label instead of one day wider.
 */
function toInclusiveSpanDays(selectedDays: number): number {
  return clampWindowDays(selectedDays) - 1;
}

function RankTrackerWindowPicker({
  selectedDays,
  onChange,
}: {
  selectedDays: number;
  onChange: (days: number) => void;
}) {
  // AUT-356: was a bare `<select>`, the one range control on the app that looked nothing like the
  // others. `allowCustom={false}` is the style guide's own "date range without custom" variation,
  // so no Custom entry renders and WEB-008's guarantee holds -- the control still has no affordance
  // that could produce a span outside WINDOW_PRESETS, and so none wider than NFR-1's 90 days.
  return (
    <DateRangePicker
      selectedDays={selectedDays}
      onDaysChange={onChange}
      allowCustom={false}
      ariaLabel="Date range"
      presets={WINDOW_PRESETS.map((p) => ({ key: String(p.days), label: p.label, days: p.days }))}
    />
  );
}

const RankTracker = () => {
  const readOnly = useViewerReadOnly();
  const { selectedBrandId, brands } = useAppData();

  const [selectedAsins, setSelectedAsins] = useState<number[]>([]);
  const hasInitializedAsins = useRef(false);
  const [selectedDays, setSelectedDays] = useState(DEFAULT_WINDOW_DAYS);

  const { dateFrom, dateTo } = useMemo(
    () => resolveDateRange(toInclusiveSpanDays(selectedDays), NO_CUSTOM_RANGE),
    [selectedDays],
  );

  const {
    data: dailyRanks,
    isLoading: isDailyRanksLoading,
    isError: isDailyRanksError,
    refetch: refetchDailyRanks,
  } = rankTrackerApi.useGetDailyRanks(selectedBrandId, dateFrom, dateTo);

  // heroProducts, brandSummary and selectedAsins stay on this page rather than moving into
  // RankHeatmapTable: handleShareReport below still assembles PublicRankTrackerPagePayload from
  // exactly these three (heroProducts, brandSummary, selectedAsinIds: selectedAsins), and the
  // public share view still renders the old flat AsinProductsTable against that payload. Deleting
  // any of the three breaks Share Report.
  const { data: heroProducts = [] } = rankTrackerApi.useGetRankTrackerHeroProducts(selectedBrandId);
  const { data: brandHeroProducts = [] } = productApi.useGetHeroProductList(selectedBrandId ?? undefined);
  const { data: rankReports = [] } = rankTrackerApi.useGetRankTrackerReports(selectedBrandId);
  const { mutateAsync: addKeywords } = rankTrackerApi.useAddRankTrackerKeywords();
  const { mutateAsync: bulkUpdateMasterList } = productApi.useBulkUpdateMasterList();
  const { mutateAsync: generateReport, isPending: isGeneratingReport } = rankTrackerApi.useGenerateRankTrackerReport();
  const { data: brandSummary = null } = rankTrackerApi.useGetBrandSummary(
    selectedBrandId,
    dateFrom,
    dateTo,
    selectedAsins,
  );
  const { refetch: fetchAllKeywords } = rankTrackerApi.useGetAllRankTrackerKeywordsForBrand(selectedBrandId);

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sharePayload, setSharePayload] = useState<PublicRankTrackerPagePayload | null>(null);
  const [addKeywordsModalOpen, setAddKeywordsModalOpen] = useState(false);
  const [selectedHeroAsin, setSelectedHeroAsin] = useState<string>("");
  const [isAssemblingShare, setIsAssemblingShare] = useState(false);

  // WEB-005: one target for all three levels (keyword/theme/product), discriminated on `level`
  // (`decisions/the-rank-detail-view-is-one-modal-over-a-discriminated-target.md`) -- in place of
  // the three-field `openKeyword` this replaces.
  const [openDetail, setOpenDetail] = useState<RankDetailTarget | null>(null);

  // WEB-010 (design-conformance pass, item 7): the keyword detail modal's own range picker needs up
  // to the full 90 days, which is already the backend maximum -- fetched once here (never inside the
  // modal, which owns no request of its own) and only while the modal is open, so opening it never
  // costs a request for a brand nobody is inspecting a keyword on. `toInclusiveSpanDays`/
  // `resolveDateRange` compute this exactly the way the table's own `dateFrom`/`dateTo` above do, so
  // when the table's own preset is already 90 the two produce identical strings, the two queries
  // share one cache entry (`getDailyRanksQueryKey`), and TanStack Query dedupes: no second request.
  const { dateFrom: extendedDateFrom, dateTo: extendedDateTo } = useMemo(
    () => resolveDateRange(toInclusiveSpanDays(MAX_WINDOW_DAYS), NO_CUSTOM_RANGE),
    [],
  );
  const { data: extendedDailyRanks } = rankTrackerApi.useGetDailyRanks(
    selectedBrandId,
    extendedDateFrom,
    extendedDateTo,
    openDetail !== null,
  );

  // FR-15's third sentence needs to know whether the brand has hero products at all -- read off
  // the daily-ranks response (`heroProductCount`), never off `heroProducts.length` from
  // `useGetRankTrackerHeroProducts`, so the header's action cluster and the empty-state branch
  // below can never disagree about it (they read the same value). Sticky across a reload of this
  // brand's own window (no `placeholderData` on this query means `dailyRanks` goes briefly
  // `undefined` on every window change) so the header's controls -- this window picker included --
  // do not blink out every time the user changes the window that drives them; it resets to
  // "unknown" only on an actual brand change, below.
  const lastKnownHeroProductCountRef = useRef<number | null>(null);
  if (dailyRanks) {
    lastKnownHeroProductCountRef.current = dailyRanks.heroProductCount;
  }
  const heroProductCount = dailyRanks?.heroProductCount ?? lastKnownHeroProductCountRef.current ?? 0;
  const hasHeroProducts = heroProductCount > 0;
  // Never true while this brand's very first load is still in flight (the table owns that state,
  // FR-13's Loading acceptance criterion) and never true behind an error (the table's error
  // branch owns that) -- only once a response has actually said there is nothing here.
  const showEmptyBrandState = !isDailyRanksLoading && !isDailyRanksError && !hasHeroProducts;

  useEffect(() => {
    if (heroProducts.length > 0 && !hasInitializedAsins.current) {
      hasInitializedAsins.current = true;
      setSelectedAsins(heroProducts.map(p => p.id));
    }
  }, [heroProducts]);

  // FR-16: nothing from the previous brand survives a switch. RankHeatmapTable resets its own
  // selection, search, filters and expansion off the `brandId` prop it is handed below; this page
  // resets what it alone owns -- the window (back to the default preset), the open keyword modal,
  // and the "unknown" state for the sticky hero-product count above.
  useEffect(() => {
    hasInitializedAsins.current = false;
    setSelectedAsins([]);
    setSelectedDays(DEFAULT_WINDOW_DAYS);
    setOpenDetail(null);
    lastKnownHeroProductCountRef.current = null;
  }, [selectedBrandId]);

  const handleAddKeywords = async (keywords: string[], starredKeywords: string[]) => {
    if (!selectedBrandId || !selectedHeroAsin) return;

    const heroAsin = selectedHeroAsin;
    setAddKeywordsModalOpen(false);
    setSelectedHeroAsin("");

    try {
      await addKeywords({
        brandId: selectedBrandId,
        heroAsin,
        keywords,
        source: 'manual',
      });

      toast({
        title: "Keywords Added",
        description: `${keywords.length} keyword${keywords.length !== 1 ? "s" : ""} added to tracking.`,
      });

      if (starredKeywords.length > 0) {
        const heroProduct = brandHeroProducts.find((p) => p.asin === heroAsin);
        if (heroProduct) {
          const allKeywords = await productApi.fetchHeroProductKeywords(
            String(heroProduct.id),
            selectedBrandId,
          );
          const starredSet = new Set(starredKeywords.map((k) => k.toLowerCase()));
          const starredIds = allKeywords
            .filter((k) => starredSet.has(k.search.toLowerCase()))
            .map((k) => k.id);
          if (starredIds.length > 0) {
            await bulkUpdateMasterList({
              productId: heroProduct.id,
              keywordIds: starredIds,
              action: KeywordMasterListAction.ADD,
            });
          }
        }
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleShareReport = async () => {
    if (!selectedBrandId) return;
    setIsAssemblingShare(true);
    try {
      const result = await fetchAllKeywords();
      const bulkKeywords = result.data ?? [];
      const keywordsByHeroProductId = Object.fromEntries(
        bulkKeywords.map(b => [b.heroProductId, b.keywords]),
      ) as Record<number, (typeof bulkKeywords)[number]['keywords']>;

      const brandName = brands.find(b => b.id === selectedBrandId)?.name ?? "";

      const payload: PublicRankTrackerPagePayload = {
        brand: { id: selectedBrandId, name: brandName },
        generatedAt: new Date().toISOString(),
        filters: {
          selectedDays,
          customDateRange: {
            from: NO_CUSTOM_RANGE.from ? NO_CUSTOM_RANGE.from.toISOString() : null,
            to: NO_CUSTOM_RANGE.to ? NO_CUSTOM_RANGE.to.toISOString() : null,
          },
          selectedAsinIds: selectedAsins,
        },
        aiReport: rankReports[0] ?? null,
        brandSummary,
        heroProducts,
        keywordsByHeroProductId,
      };

      setSharePayload(payload);
      setShareModalOpen(true);
    } catch {
      sonnerToast.error("Failed to assemble share data. Please try again.");
    } finally {
      setIsAssemblingShare(false);
    }
  };

  // WEB-010 (design-conformance pass, items 3+6): the window picker's own state stays here --
  // `dateFrom`/`dateTo` above are derived from `selectedDays` and drive `useGetDailyRanks` -- but the
  // control itself now renders inside RankHeatmapTable's toolbar row, on the same line as the search
  // box, Filters and the count line, right-aligned. Building the element once and handing it down as
  // a node (rather than exporting WINDOW_PRESETS/clampWindowDays/RankTrackerWindowPicker out of this
  // file) keeps the NFR-1 arithmetic and its regression test exactly where they are.
  const windowPicker = <RankTrackerWindowPicker selectedDays={selectedDays} onChange={setSelectedDays} />;

  return (
    <>
      <div className="-m-9 p-9 bg-card space-y-6 min-h-screen">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <PageTitle title="Rank Tracker" brandScoped />
            <p className="text-muted-foreground">
              Monitor your keyword rankings over time to measure if your SEO decisions are working.
            </p>
          </div>
          {hasHeroProducts && !readOnly && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline-on-white"
                disabled={isGeneratingReport || !selectedBrandId}
                onClick={async () => {
                  if (!selectedBrandId) return;

                  await generateReport({ brandId: selectedBrandId });
                }}
              >
                <Sparkles className="h-4 w-4" />
                {isGeneratingReport ? "Generating..." : "Generate Report"}
              </Button>
              <Button onClick={() => setAddKeywordsModalOpen(true)}>
                <Plus className="h-4 w-4" />
                Add Keyword
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="More actions">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={handleShareReport}
                    disabled={isAssemblingShare}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    {isAssemblingShare ? "Preparing..." : "Share Report"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {showEmptyBrandState ? (
          <EmptyBrandState
            pageName="Rank Tracker"
            skeleton="rank-tracker"
            keywordCta={readOnly ? undefined : {
              onClick: () => setAddKeywordsModalOpen(true),
            }}
          />
        ) : (
          <>
            {/* WEB-010 (design-conformance pass, item 2): the human asked to put the AI card back
                above the table after seeing both screens side by side -- this reverses WEB-008's
                FR-8 ("leads with the table"), a shipped requirement in this working tree. The
                RankTracker.test.tsx case asserting the old order is updated to assert this one;
                the intent.md/PLATFORM.md record of the reversal is being handled separately. */}
            <SignalAIRankReport />

            <RankHeatmapTable
              brandId={selectedBrandId}
              dateFrom={dateFrom}
              dateTo={dateTo}
              data={dailyRanks}
              isLoading={isDailyRanksLoading}
              isError={isDailyRanksError}
              refetch={refetchDailyRanks}
              windowPicker={windowPicker}
              onOpenDetail={setOpenDetail}
            />
          </>
        )}
      </div>

      {openDetail && (
        <KeywordRankDetailModal
          open
          onOpenChange={(open) => {
            if (!open) setOpenDetail(null);
          }}
          target={openDetail}
          days={dailyRanks?.days ?? []}
          extendedRanks={extendedDailyRanks}
          adSpendEndDate={dailyRanks?.adSpendEndDate ?? null}
        />
      )}

      {sharePayload && !readOnly && (
        <ShareLinkModal
          open={shareModalOpen}
          onOpenChange={(open) => {
            setShareModalOpen(open);
            if (!open) setSharePayload(null);
          }}
          shareableType="rank_tracker_page"
          frozenPayload={sharePayload as unknown as Record<string, unknown>}
          resourceName="Rank Tracker"
        />
      )}
      {!readOnly ? (
        <AddKeywordsModal
          open={addKeywordsModalOpen}
          onOpenChange={(open) => {
            setAddKeywordsModalOpen(open);
            if (!open) setSelectedHeroAsin("");
          }}
          onAddKeywords={handleAddKeywords}
          heroAsin={selectedHeroAsin}
          onHeroAsinChange={setSelectedHeroAsin}
          heroProducts={brandHeroProducts}
          showMasterListOption
        />
      ) : null}
    </>
  );
};

export default RankTracker;
